import React from 'react';
import SEO from "@/components/SEO";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";

const OccitanieIndex = () => {
  const departments = [
    { code: "12", name: "Aveyron", capital: "Rodez", description: "Siège social et expertise historique" },
    { code: "30", name: "Gard", capital: "Nîmes", description: "Service complet de gestion des déchets" },
    { code: "34", name: "Hérault", capital: "Montpellier", description: "Solutions pour métropole dynamique" },
    { code: "48", name: "Lozère", capital: "Mende", description: "Services adaptés au territoire rural" },
    { code: "81", name: "Tarn", capital: "Albi", description: "Partenaire industriel et artisanal" },
    { code: "82", name: "Tarn-et-Garonne", capital: "Montauban", description: "Valorisation et économie circulaire" },
    { code: "11", name: "Aude", capital: "Carcassonne", description: "Recyclage et collecte spécialisée" },
    { code: "66", name: "Pyrénées-Orientales", capital: "Perpignan", description: "Solutions transfrontalières" }
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <SEO 
        title="Gestion des Déchets en Occitanie | Groupe Fabre Environnement - 8 Départements"
        description="Expert gestion déchets Occitanie : Aveyron, Gard, Hérault, Lozère, Tarn, Tarn-et-Garonne, Aude, Pyrénées-Orientales. Services de proximité depuis 35 ans."
        canonical="/occitanie"
      />
      
      <Navbar />
      
      <main className="flex-grow pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-fabre-green via-fabre-teal to-fabre-darkgreen text-white py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-6xl font-heading font-bold mb-6">
                Gestion des Déchets en Occitanie
              </h1>
              <p className="text-xl leading-relaxed mb-8 opacity-90">
                35 ans d'expertise au service de 8 départements pour une gestion responsable de vos déchets
              </p>
              <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-6 py-3">
                <span className="text-2xl">🗺️</span>
                <span className="font-medium">8 départements couverts</span>
              </div>
            </div>
          </div>
        </section>

        {/* Présentation Occitanie */}
        <section className="py-20 bg-gradient-to-br from-white to-fabre-lightgreen/10">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <h2 className="text-3xl font-heading font-bold text-fabre-gray mb-6">
                Notre Expertise Régionale
              </h2>
              <p className="text-lg text-fabre-darkteal leading-relaxed">
                Depuis notre création en 1988, nous avons développé une connaissance approfondie des spécificités 
                de chaque territoire occitan. De l'Aveyron rural aux métropoles de Montpellier et Toulouse, 
                nous adaptons nos solutions aux besoins locaux.
              </p>
            </div>

            {/* Statistiques régionales */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
              <div className="bg-white rounded-xl p-6 text-center shadow-lg">
                <div className="text-3xl font-bold text-fabre-green mb-2">35+</div>
                <div className="text-sm text-fabre-darkteal">Années d'expérience</div>
              </div>
              <div className="bg-white rounded-xl p-6 text-center shadow-lg">
                <div className="text-3xl font-bold text-fabre-teal mb-2">8</div>
                <div className="text-sm text-fabre-darkteal">Départements</div>
              </div>
              <div className="bg-white rounded-xl p-6 text-center shadow-lg">
                <div className="text-3xl font-bold text-fabre-darkgreen mb-2">500+</div>
                <div className="text-sm text-fabre-darkteal">Villes desservies</div>
              </div>
              <div className="bg-white rounded-xl p-6 text-center shadow-lg">
                <div className="text-3xl font-bold text-fabre-green mb-2">100%</div>
                <div className="text-sm text-fabre-darkteal">Éco-responsable</div>
              </div>
            </div>
          </div>
        </section>

        {/* Départements */}
        <section className="py-20 bg-gradient-to-br from-fabre-lightgray/30 to-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-heading font-bold text-center text-fabre-gray mb-16">
              Nos Zones d'Intervention
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {departments.map((dept) => (
                <Link 
                  key={dept.code}
                  to={`/services/departement/${dept.code}`}
                  className="group bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-fabre-green/10"
                >
                  <div className="text-center mb-4">
                    <div className="text-2xl font-bold text-fabre-green mb-1">{dept.code}</div>
                    <h3 className="text-xl font-bold text-fabre-darkgreen group-hover:text-fabre-green transition-colors">
                      {dept.name}
                    </h3>
                    <div className="text-sm text-fabre-darkteal">{dept.capital}</div>
                  </div>
                  <p className="text-sm text-fabre-darkteal text-center mb-4">
                    {dept.description}
                  </p>
                  <div className="text-center">
                    <span className="text-fabre-green font-medium group-hover:underline">
                      Découvrir →
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Services par Territoire */}
        <section className="py-20 bg-gradient-to-r from-fabre-green/10 to-fabre-teal/10">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-heading font-bold text-center text-fabre-gray mb-16">
              Services Adaptés aux Territoires
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-fabre-darkgreen mb-4">
                  🏔️ Territoires Ruraux
                </h3>
                <p className="text-fabre-darkteal mb-4">
                  Aveyron, Lozère : Solutions adaptées aux contraintes géographiques, 
                  collecte dans les zones isolées, service personnalisé.
                </p>
                <ul className="text-sm text-fabre-darkteal space-y-2">
                  <li>• Collecte en zones rurales</li>
                  <li>• Services agricoles spécialisés</li>
                  <li>• Flexibilité horaires</li>
                </ul>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-fabre-darkgreen mb-4">
                  🏙️ Zones Urbaines
                </h3>
                <p className="text-fabre-darkteal mb-4">
                  Hérault, Gard : Solutions métropolitaines, gestion des grands volumes, 
                  partenariats avec collectivités.
                </p>
                <ul className="text-sm text-fabre-darkteal space-y-2">
                  <li>• Collecte urbaine haute fréquence</li>
                  <li>• Gestion chantiers BTP</li>
                  <li>• Solutions logistiques optimisées</li>
                </ul>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-fabre-darkgreen mb-4">
                  🏭 Zones Industrielles
                </h3>
                <p className="text-fabre-darkteal mb-4">
                  Tarn, Tarn-et-Garonne : Partenariat avec l'industrie locale, 
                  solutions sur-mesure pour déchets spéciaux.
                </p>
                <ul className="text-sm text-fabre-darkteal space-y-2">
                  <li>• Déchets industriels spécialisés</li>
                  <li>• Contrats sur-mesure</li>
                  <li>• Optimisation des coûts</li>
                </ul>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-fabre-darkgreen mb-4">
                  🌊 Littoral Méditerranéen
                </h3>
                <p className="text-fabre-darkteal mb-4">
                  Aude, Pyrénées-Orientales : Gestion saisonnière, tourisme, 
                  défis environnementaux spécifiques.
                </p>
                <ul className="text-sm text-fabre-darkteal space-y-2">
                  <li>• Adaptation flux touristiques</li>
                  <li>• Protection environnement littoral</li>
                  <li>• Services été renforcés</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Régional */}
        <section className="py-20 bg-gradient-to-br from-fabre-gray/5 to-fabre-green/5">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-heading font-bold text-fabre-gray mb-8">
              Votre Partenaire Local
            </h2>
            <p className="text-xl text-fabre-darkteal mb-12 max-w-3xl mx-auto">
              Quel que soit votre département en Occitanie, nos équipes locales 
              vous accompagnent avec des solutions adaptées à votre territoire.
            </p>
            <Link 
              to="/contact"
              className="inline-flex items-center gap-3 bg-fabre-green hover:bg-fabre-darkgreen text-white px-8 py-4 rounded-lg font-medium transition-colors"
            >
              <span>Contactez votre agence locale</span>
              <span>→</span>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default OccitanieIndex;